/**
 * Product Breadcrumbs Handler
 * Manages the "Back to Weekly Menu" breadcrumb link logic
 */

class ProductBreadcrumbs {
  constructor() {
    this.breadcrumbElement = document.querySelector(
      ".product-breadcrumb-back-to-menu",
    );
    this.linkElement = this.breadcrumbElement?.querySelector(
      ".product-breadcrumb-back-to-menu__link",
    );

    if (this.breadcrumbElement && this.linkElement) {
      this.init();
    }
  }

  init() {
    this.updateBreadcrumbLink();

    document.addEventListener("weeklyMenu:changed", () => {
      this.updateBreadcrumbLink();
    });
  }

  updateBreadcrumbLink() {
    const link = this.determineBreadcrumbLink();

    if (link && this.linkElement) {
      this.linkElement.href = link.url;
      const spanElement = this.linkElement.querySelector("span");
      if (spanElement) {
        spanElement.textContent = link.text;
      }
    } else if (this.breadcrumbElement) {
      // Hide the breadcrumb if no appropriate link can be determined
      this.breadcrumbElement.style.display = "none";
    }
  }

  determineBreadcrumbLink() {
    const productData = this.getProductData();

    if (productData && productData.product?.type === "Package") {
      const link = this.getPackageBackLink();
      if (link) {
        return link;
      }
    }

    // Special case: Gift Card products
    if (productData && productData.product?.type === "GIFT_CARD") {
      return this.getGiftingPageLink();
    }

    if (!window.WeeklyMenuUtils) {
      return this.getFallbackLink();
    }

    // Case 1: User has previously selected a weekly menu (check localStorage)
    const sessionData = window.WeeklyMenuUtils.getSessionData();
    if (sessionData.weeklyMenuCollectionHandle) {
      return {
        url: `/collections/${sessionData.weeklyMenuCollectionHandle}`,
        text: "Back to weekly menu",
      };
    }

    // Case 2: User hasn't selected a weekly menu but product only has one weekly menu available
    if (productData && productData.weeklyMenus) {
      const availableMenus = window.WeeklyMenuUtils.getAvailableWeeklyMenus(
        productData.weeklyMenus,
      );

      if (availableMenus.length === 1) {
        const menu = availableMenus[0];
        // Try multiple properties to get the collection handle
        const collectionHandle =
          menu.collection_handle ||
          menu.handle ||
          menu.name ||
          (menu.collection && menu.collection.handle);

        if (collectionHandle) {
          return {
            url: `/collections/${collectionHandle}`,
            text: "Back to weekly menu",
          };
        }
      }
    }

    // Case 3: Fallback to /pages/menus
    return this.getFallbackLink();
  }

  getPackageBackLink() {
    if (document.referrer && document.referrer.includes("/gifting")) {
      return this.getGiftingPageLink();
    }
  }

  getGiftingPageLink() {
    // Check if window.pages exists and find the gifting page
    if (!window.pages || !Array.isArray(window.pages)) {
      return null;
    }

    const giftingPage = window.pages.find((page) => {
      const title = (page.title || "").toLowerCase();
      const url = (page.url || "").toLowerCase();
      return title.includes("gifting") || url.includes("gifting");
    });

    if (giftingPage) {
      return {
        url: giftingPage.url,
        text: "Back to weekly menu",
      };
    }

    return null;
  }

  getFallbackLink() {
    return {
      url: "/pages/menu",
      text: "Back to menu",
    };
  }

  getProductData() {
    // Try to get product data from global variables or DOM
    if (window.productData) {
      return window.productData;
    }

    // Try to get from product data script tag
    const productHandle = this.getProductHandle();
    if (productHandle) {
      const productDataElement = document.getElementById(
        `product-data-${productHandle}`,
      );
      if (productDataElement) {
        try {
          return JSON.parse(productDataElement.textContent);
        } catch (error) {
          console.error("Error parsing product data:", error);
        }
      }
    }

    return null;
  }

  getProductHandle() {
    // Try to get product handle from various sources
    if (window.productHandle) {
      return window.productHandle;
    }

    // Try to get from URL path
    const pathParts = window.location.pathname.split("/");
    const productIndex = pathParts.indexOf("products");
    if (productIndex !== -1 && pathParts[productIndex + 1]) {
      return pathParts[productIndex + 1];
    }

    // Try to get from product-info element
    const productInfo = document.querySelector("product-info");
    if (productInfo && productInfo.dataset.url) {
      const urlParts = productInfo.dataset.url.split("/");
      const productUrlIndex = urlParts.indexOf("products");
      if (productUrlIndex !== -1 && urlParts[productUrlIndex + 1]) {
        return urlParts[productUrlIndex + 1];
      }
    }

    return null;
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  new ProductBreadcrumbs();
});

// Also initialize if script is loaded after DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new ProductBreadcrumbs();
  });
} else {
  new ProductBreadcrumbs();
}
